import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";

// Geschäftszeiten für die Terminvorschläge (W9).
//
// Die Klage, die das auslöst: „Ich bekomme Termine vor 9 Uhr vorgeschlagen, die
// will ich nie." Bis dahin waren 8-18 Uhr im Kalender-Dienst hart verdrahtet
// (`services/calendar.py::BIZ_START_HOUR/BIZ_END_HOUR` im bitop-email-service).
//
// PERSISTENZ: server-seitig am NUTZER (`user_settings.payload
// .calendar_business_hours` via GET/PUT /settings), NICHT im localStorage —
// dieselbe Begründung und derselbe Weg wie bei `mailInboxDepth.ts`: es ist eine
// Einstellung des Menschen, keine des Browsers, und gilt geräteübergreifend.
//
// WICHTIG, und der Grund, warum dieser Wert im Wizard NICHT mitgeschickt wird:
// die Slot-Suche liest ihn SERVERSEITIG aus dem Konto des Token-Users
// (`app/routers/calendar.py::_business_hours`). Dieses Modul ist nur für die
// ANZEIGE und das Einstellen zuständig. Würde der Wizard den Wert in den
// Request legen, hinge die Zusage „kein Vorschlag vor 9 Uhr" am jeweils
// ausgelieferten Frontend statt am Nutzer — ein alter Tab hätte sie ausgehebelt.

export interface BusinessHours {
  start_hour: number;
  end_hour: number;
}

/** Die Haus-Regel 9-17 — dieselbe Zahl wie serverseitig in
 *  `app/schemas/settings.py::DEFAULT_BIZ_START_HOUR/DEFAULT_BIZ_END_HOUR` und im
 *  Kalender-Dienst (`services/calendar.py::BIZ_START_HOUR/BIZ_END_HOUR`).
 *
 *  Sie steht hier NUR fuer die Anzeige, solange `/settings` noch nicht geladen
 *  ist: gerechnet wird immer serverseitig. Trotzdem muss sie stimmen — eine
 *  abweichende Zahl hier hiesse, die Oberflaeche nennt „08:00-18:00", waehrend
 *  der Server 09-17 anwendet. Genau das war der Zustand bis W9. */
export const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  start_hour: 9,
  end_hour: 17,
};

/** Wählbare Stunden. Volle Stunden, weil die Maske im Kalender-Dienst auf
 *  Stunden greift — „ab 8:45" wäre eine Genauigkeit, die der Motor dahinter
 *  nicht einlöst. 6-12 als Beginn und 12-22 als Ende decken jede reale
 *  Arbeitszeit ab, ohne 24 Einträge in ein Auswahlfeld zu kippen. */
export const START_HOUR_CHOICES = [6, 7, 8, 9, 10, 11, 12];
export const END_HOUR_CHOICES = [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22];

export function hourLabel(h: number): string {
  return `${String(h).padStart(2, "0")}:00`;
}

export function isBusinessHours(v: unknown): v is BusinessHours {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.start_hour === "number" &&
    typeof o.end_hour === "number" &&
    Number.isInteger(o.start_hour) &&
    Number.isInteger(o.end_hour) &&
    o.start_hour >= 0 &&
    o.start_hour <= 23 &&
    o.end_hour >= 1 &&
    o.end_hour <= 24 &&
    o.end_hour > o.start_hour
  );
}

/** Nur das Feld, das uns interessiert — der Rest von SettingsOut ist egal. */
interface HoursSettingsResponse {
  calendar_business_hours?: unknown;
}

export interface UseBusinessHours {
  hours: BusinessHours;
  /** false, solange die Nutzer-Einstellung noch geladen wird. */
  ready: boolean;
  /** true, wenn die Einstellung NICHT gelesen werden konnte.
   *
   *  `hours` steht dann auf dem Default — aber das ist eine Annahme, keine
   *  Auskunft: der Server rechnet mit dem echten Wert des Kontos weiter, und
   *  der kann ein anderer sein. Wer die Zeiten ANZEIGT, muss diesen Fall
   *  unterscheiden können, sonst behauptet die Oberfläche „Vorschläge zwischen
   *  08:00 und 18:00", während der Server 09-17 anwendet — eine Zusage über
   *  einen Zustand, den diese Seite gerade nicht kennt.
   *
   *  Fürs Einstellen ist der Default weiterhin brauchbar (der PUT schreibt ja,
   *  was der Nutzer wählt) — deshalb ein eigenes Flag statt eines Fehlers. */
  failed: boolean;
  saving: boolean;
  /** Optimistisch: setzt sofort + schreibt via PUT /settings. Bei Fehlschlag
   *  wird zurückgerollt. Liefert, ob gespeichert wurde. */
  setHours: (h: BusinessHours) => Promise<boolean>;
}

export function useBusinessHours(): UseBusinessHours {
  const [hours, setHoursRaw] = useState<BusinessHours>(DEFAULT_BUSINESS_HOURS);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    void api
      .get<HoursSettingsResponse>("/settings")
      .then((res) => {
        if (!aliveRef.current) return;
        // Defensiv: fehlendes/kaputtes Feld (z.B. älteres Backend) darf die
        // Seite NICHT mitreißen — dann gilt schlicht der Default.
        const v = res?.calendar_business_hours;
        if (isBusinessHours(v)) setHoursRaw({ start_hour: v.start_hour, end_hour: v.end_hour });
        // Ein FEHLENDES/kaputtes Feld ist etwas anderes als ein Fehlschlag:
        // der Server hat geantwortet, er kennt das Feld nur (noch) nicht →
        // dann gilt der Default und das ist auch die Wahrheit.
      })
      .catch(() => {
        /* Settings nicht lesbar. Kein Toast — aber auch keine Zahl behaupten:
           `failed` sagt dem Aufrufer, dass `hours` nur eine Annahme ist. */
        if (aliveRef.current) setFailed(true);
      })
      .finally(() => {
        if (aliveRef.current) setReady(true);
      });
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const setHours = useCallback(async (h: BusinessHours): Promise<boolean> => {
    // Ein leeres Fenster gar nicht erst abschicken: der Server lehnt es mit 422
    // ab, aber die optimistische Anzeige stünde dann kurz auf einem Wert, der
    // nie gilt.
    if (!isBusinessHours(h)) return false;
    let prev: BusinessHours = DEFAULT_BUSINESS_HOURS;
    setHoursRaw((cur) => {
      prev = cur;
      return h;
    });
    setSaving(true);
    try {
      // Partieller PUT: der Server merged nur dieses Feld.
      await api.put("/settings", { calendar_business_hours: h });
      // Erfolgreich geschrieben heisst: der Server haelt jetzt diesen Wert —
      // die Anzeige darf ihn wieder nennen, auch wenn das Lesen vorhin scheiterte.
      if (aliveRef.current) setFailed(false);
      return true;
    } catch {
      if (aliveRef.current) setHoursRaw(prev);
      return false;
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  }, []);

  return { hours, ready, failed, saving, setHours };
}
